// Sistema exclusivo de notificações push mobile (Android/iOS)
// Este módulo funciona APENAS no mobile e permite notificações mesmo com navegador fechado
// Separado completamente do sistema de notificações desktop/admin

let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
let isInitialized = false;

// Detectar se é mobile
const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Detectar se é Android
const isAndroid = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

// Detectar se é iOS
const isIOS = (): boolean => {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

/**
 * Inicializar sistema de notificações push mobile
 * Deve ser chamado apenas uma vez quando a aplicação carrega
 */
export const initializeMobilePushNotifications = async (): Promise<boolean> => {
  // Só funciona no mobile
  if (!isMobile()) {
    console.log('📱 Sistema de notificações push mobile: não é mobile, desabilitado');
    return false;
  }

  // Evitar inicialização duplicada
  if (isInitialized) {
    console.log('📱 Sistema de notificações push mobile: já inicializado');
    return true;
  }

  console.log('📱 Inicializando sistema de notificações push mobile...');
  console.log('📱 Dispositivo:', {
    isAndroid: isAndroid(),
    isIOS: isIOS(),
    userAgent: navigator.userAgent,
  });

  // Verificar suporte a Service Worker
  if (!('serviceWorker' in navigator)) {
    console.warn('⚠️ Service Worker não suportado neste navegador');
    return false;
  }

  // Verificar suporte a Notificações
  if (!('Notification' in window)) {
    console.warn('⚠️ Notificações não suportadas neste navegador');
    return false;
  }

  try {
    // Registrar Service Worker
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    serviceWorkerRegistration = registration;
    isInitialized = true;

    console.log('✅ Service Worker registrado para mobile push:', registration.scope);
    console.log('📱 Estado do Service Worker:', {
      installing: registration.installing?.state,
      waiting: registration.waiting?.state,
      active: registration.active?.state,
      controller: !!navigator.serviceWorker.controller,
    });

    // Aguardar Service Worker estar pronto
    if (registration.installing) {
      registration.installing.addEventListener('statechange', () => {
        if (registration.installing?.state === 'activated') {
          console.log('✅ Service Worker ativado para mobile push');
        }
      });
    } else if (registration.active) {
      console.log('✅ Service Worker já está ativo para mobile push');
    }

    // Solicitar permissão para notificações
    if (Notification.permission === 'default') {
      console.log('📱 Solicitando permissão para notificações push...');
      
      // No iOS, só pode solicitar após interação do usuário
      if (isIOS()) {
        console.log('📱 iOS detectado: permissão será solicitada na primeira interação');
        // Criar listener para primeira interação
        const requestPermissionOnInteraction = () => {
          if (Notification.permission === 'default') {
            Notification.requestPermission().then((permission) => {
              console.log('📱 Permissão iOS:', permission);
            });
          }
          document.removeEventListener('touchstart', requestPermissionOnInteraction);
          document.removeEventListener('click', requestPermissionOnInteraction);
        };
        document.addEventListener('touchstart', requestPermissionOnInteraction, { once: true });
        document.addEventListener('click', requestPermissionOnInteraction, { once: true });
      } else {
        // Android: pode solicitar automaticamente
        const permission = await Notification.requestPermission();
        console.log('📱 Permissão Android:', permission);
      }
    } else {
      console.log('📱 Permissão já concedida:', Notification.permission);
    }

    return true;
  } catch (error) {
    console.error('❌ Erro ao inicializar notificações push mobile:', error);
    return false;
  }
};

/**
 * Verificar se o sistema está pronto para enviar notificações push
 */
export const isMobilePushReady = (): boolean => {
  if (!isMobile()) return false;
  if (!isInitialized) return false;
  if (!serviceWorkerRegistration) return false;
  if (Notification.permission !== 'granted') return false;
  
  return serviceWorkerRegistration.active !== null || serviceWorkerRegistration.installing !== null;
};

/**
 * Enviar notificação push mobile (barra do sistema Android/iOS)
 * Funciona mesmo com navegador fechado ou em segundo plano
 */
export const showMobilePushNotification = async (
  title: string,
  message: string,
  options?: {
    icon?: string;
    badge?: string;
    tag?: string;
    data?: any;
    vibrate?: number[];
    requireInteraction?: boolean;
  }
): Promise<boolean> => {
  // Só funciona no mobile
  if (!isMobile()) {
    console.log('📱 Notificação push mobile: não é mobile, ignorando');
    return false;
  }

  // Verificar se está pronto
  if (!isMobilePushReady()) {
    console.warn('⚠️ Sistema de notificações push mobile não está pronto');
    return false;
  }

  // Verificar permissão
  if (Notification.permission !== 'granted') {
    console.warn('⚠️ Permissão de notificação não concedida');
    return false;
  }

  try {
    const notificationIcon = options?.icon || '/assets/images/image.png';
    const notificationBadge = options?.badge || '/assets/images/image.png';
    
    console.log('📱 Enviando notificação push mobile:', {
      title,
      message,
      icon: notificationIcon,
      isAndroid: isAndroid(),
      isIOS: isIOS(),
    });

    // Vibrar no mobile (Android)
    if (isAndroid() && 'vibrate' in navigator) {
      try {
        navigator.vibrate(options?.vibrate || [200, 100, 200]);
        console.log('✅ Vibração ativada no Android');
      } catch (vibError) {
        console.warn('⚠️ Erro ao vibrar:', vibError);
      }
    }

    // Mostrar notificação via Service Worker (funciona mesmo com navegador fechado)
    await serviceWorkerRegistration!.showNotification(title, {
      body: message,
      icon: notificationIcon,
      badge: notificationBadge,
      tag: options?.tag || `mobile-push-${Date.now()}`,
      requireInteraction: options?.requireInteraction || false,
      silent: false, // Permitir som no Android/iOS
      ...(isMobile() && { vibrate: options?.vibrate || [200, 100, 200] }),
      data: {
        url: window.location.href,
        timestamp: Date.now(),
        ...options?.data,
      },
    });

    console.log('✅ Notificação push mobile exibida na barra do sistema');
    return true;
  } catch (error) {
    console.error('❌ Erro ao exibir notificação push mobile:', error);
    return false;
  }
};

/**
 * Verificar status do sistema de notificações push mobile
 */
export const getMobilePushStatus = () => {
  return {
    isMobile: isMobile(),
    isAndroid: isAndroid(),
    isIOS: isIOS(),
    isInitialized,
    hasServiceWorker: !!serviceWorkerRegistration,
    serviceWorkerReady: serviceWorkerRegistration?.active !== null,
    notificationPermission: 'Notification' in window ? Notification.permission : 'não suportado',
    isReady: isMobilePushReady(),
  };
};

